// Shared pieces of the 客户工作台 (VIP 服务流程 5.0, round 2): the SOP step
// catalogue (the backend serves the same list at /service/sop-catalogue — this
// copy is what the UI falls back to), the 速记卡 fields, small layout helpers
// and the client-side 下一步 fallback used while /profiles/:id/workspace is not
// deployed yet.
import React, { useEffect, useRef, useState } from 'react';
import { useTour } from '../../tour/tourCore';
import { TourCatalogue } from '../../tour/TourWidgets';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { S } from '../../staff/StaffUI';
import { toDate, overText, fmtMDHM, reportStatusKey, REPORT_STATUS } from '../../service/sopShared';
import { toPacificWallClock } from '../../../utils/pacificTime';

export { S };

export const is404 = (e) => {
  const st = e && e.response && e.response.status;
  return st === 404 || st === 405 || st === 501;
};

// ── SOP 5.0 step catalogue ────────────────────────────────────────────────
// standard = the 完成标准 text from the SOP; autoRule = what the system looks
// at when the step completes by itself.
export const SOP_STEPS = [
  { key: 'sign', name: '签约启动', owner: '面谈老师', deadline: 'Day 1', auto: true,
    standard: '面谈完成、套餐确定、签约付款、基础资料收集、CRM 建档、当晚推广启动并留存记录。',
    autoRule: '有签约合同、已付款账单或服务开始日期时自动完成。' },
  { key: 'group', name: '建立专属服务群', owner: '主服务老师', deadline: 'Day 2',
    standard: '服务群建立、自我介绍完成、会员确认画像面谈预约时间、CRM 完成预约记录。' },
  { key: 'portrait', name: '40 分钟人生合伙人画像 + S/A/B/C', owner: '主服务老师', deadline: 'Day 2–3', notePrompt: '画像要点 / S·A·B·C 分级',
    standard: '围绕「你是谁 / 想过怎样的生活 / 谁真正适合你」深度沟通，完成 S/A/B/C 择偶条件分级，交付《人生合伙人画像》。未取得会员同意，不得突破绝对底线。' },
  { key: 'love_language', name: '爱的五种语言测评', owner: '主服务老师', deadline: '画像后', auto: true, manualToo: true,
    standard: '测评完成、结果记录，老师形成简短分析并向会员解释参考意义。',
    autoRule: '会员提交「爱的五种语言」问卷后自动完成；线下做的可以手动标记。' },
  { key: 'quick_card', name: '匹配速记卡', owner: '主服务老师', deadline: '画像后 24 小时', auto: true,
    standard: '会员编号、年龄、城市、婚育、职业、经济、人物特点、核心优势、择偶条件、吸引力偏好、爱的语言、离婚原因、绝对底线、可放宽条件、最适合类型、潜在顾虑、当前服务状态。其他老师 30 秒内能看懂匹配方向；资料更新要写明原因。',
    autoRule: '「资料·报告」里的速记卡填满 8 项以上自动完成。' },
  { key: 'team_review', name: '48 小时团队过档', owner: '主服务老师 / 主管', deadline: '速记卡后 48 小时',
    standard: '画像完成后 48 小时内进入团队匹配池；每日 15 分钟过档，每周 Match Meeting。' },
  { key: 'first_recs', name: '首批推荐 3 位（两位老师核实）', owner: '主服务老师 + 核实老师', deadline: '画像后 3 天', needsVerifier: true, round: true,
    standard: '目标 3 位，每位都有具体推荐理由；两位老师核实并打勾；不允许为凑人数降低质量。达不到标准时，向会员说明真实筛选进度、原因和下次更新日期，并继续 48 小时主动联系。' },
  { key: 'rec_call', name: '推荐后 24 小时说明电话', owner: '主服务老师', deadline: '推荐后 24 小时', auto: true, round: true,
    standard: '说明为什么推荐、匹配点、对方优势、可能顾虑、见面注意事项、建议优先了解谁；记录会员第一反应。',
    autoRule: '推荐之后写了一条「推荐说明」联系记录就自动完成。' },
  { key: 'cross_call', name: '见面前交叉电话（双方）', owner: '双方老师', deadline: '见面前 24 小时', auto: true, round: true,
    standard: '女方老师打给男士、男方老师打给女士：确认见面意愿、第一印象与期待、对方优势、沟通雷区、时间地点、未解决的问题。电话后要有文字记录。',
    autoRule: '这次约会「见面前 · 本会员」和「见面前 · 对方会员」两条记录都写好后自动完成。' },
  { key: 'date_feedback', name: '约会后 24–48 小时双向反馈', owner: '双方老师', deadline: '见面后 48 小时', auto: true, round: true,
    standard: '电话回访：第一印象、吸引力、舒适度、喜欢之处、顾虑、是否愿意第二次见面、不继续的真实原因。',
    autoRule: '见面后写了「见面后回访」记录或约会反馈就自动完成。' },
  { key: 'review_or_second', name: '不合适 3 天内复盘 / 有好感约第二次', owner: '主服务老师', deadline: '见面后 3 天', choice: true, round: true,
    standard: '双方有好感 → 尽快约第二次；一方犹豫 → 分析原因，能解决的协助沟通；不合适 → 3 天内复盘，更新画像后约 2 天内启动下一轮。喜欢和拒绝的原因要具体记录（外形、年龄感、身高、性格、沟通、价值观等）。' },
  { key: 'exclusive', name: '进入一对一（暂停其他推荐）', owner: '主服务老师', deadline: '—', round: true,
    standard: '分别确认双方意愿、暂停其他推荐、在 CRM 记录确认日期。' },
  { key: 'close', name: '一对一满 3 个月 · 结单', owner: '主服务老师 / 主管', deadline: '一对一后 3 个月', round: true, notePrompt: '双方相处情况、确认关系稳定',
    standard: '进入一对一后 3 个月是关系稳定期：继续关怀，了解双方相处情况。满 3 个月关系稳定 → 主管或主服务老师确认结单，服务完成。3 个月内分手 → 记录原因，重新开始推荐。' },
];
// 一对一 → 关系稳定期 length (backend CLOSE_AFTER_DAYS).
export const CLOSE_AFTER_DAYS = 90;
// "2026-10-16" — the Pacific date of an instant (a bare 'YYYY-MM-DD' is shown as is).
export const fmtYMD = (v) => {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d0 = v ? new Date(v) : null;
  if (!d0 || isNaN(d0)) return '';
  const d = toPacificWallClock(d0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const stepDef = (key) => SOP_STEPS.find((s) => s.key === key) || null;

// ── 匹配速记卡 ─────────────────────────────────────────────────────────────
export const QUICK_FIELDS = [
  ['member_no', '会员编号'], ['age', '年龄'], ['city', '城市'], ['marriage_children', '婚育'],
  ['occupation', '职业'], ['finance', '经济情况'], ['personality', '人物特点', true], ['strengths', '核心优势', true],
  ['criteria', '择偶条件', true], ['attraction', '吸引力偏好', true], ['love_language', '爱的语言'],
  ['divorce_reason', '离婚原因', true], ['dealbreakers', '绝对底线', true], ['flexible', '可放宽条件', true],
  ['best_match_type', '最适合类型', true], ['concerns', '潜在顾虑', true], ['service_status', '当前服务状态'],
];
export const quickLabel = (k) => (QUICK_FIELDS.find((f) => f[0] === k) || [k, k])[1];

// ── light ─────────────────────────────────────────────────────────────────
export const LIGHT = {
  green: { zh: '正常', en: 'On track', dot: S.ok, bg: '#E6EEE8', fg: S.ok },
  yellow: { zh: '需要关注', en: 'Needs attention', dot: S.goldDecor, bg: '#F3EADB', fg: S.warn },
  red: { zh: '必须处理', en: 'Act now', dot: S.bad, bg: '#F3E3E1', fg: S.bad },
};
export const SEVERITY_DOT = { red: S.bad, amber: S.goldDecor, normal: '#A39E95' };

// ── small UI helpers ──────────────────────────────────────────────────────
// One grey "怎么用" sentence at the top of a tab, with a 「这是什么？」 button that unfolds the
// one-line explanation of this tab plus the 操作引导 catalogue (✓ seen / current / to do, 「从第 N 步继续」).
// `explain` is that one line (falls back to the old numbered `steps`). The 「操作引导」 menu's
// 「只看「这个页签怎么用」」 opens it too (tour.introTick).
export function TabIntro({ text, steps = [], explain = '', isZh = true }) {
  const tour = useTour();
  const [open, setOpen] = useState(false);
  const tick = tour.introTick;
  const seenTick = useRef(tick);
  useEffect(() => {
    if (tick !== seenTick.current) { seenTick.current = tick; setOpen(true); }
  }, [tick]);
  const hasMore = !!explain || steps.length > 0 || tour.available;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, color: S.sub, lineHeight: 1.6 }}>{text}</span>
        {hasMore && (
          <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${open ? S.ink : S.line}`, background: open ? S.soft : 'transparent', borderRadius: S.radius,
              height: 28, padding: '0 12px', cursor: 'pointer', color: S.gold, fontSize: 12, fontWeight: 400, letterSpacing: '.14em', fontFamily: 'inherit' }}>
            <HelpCircle size={13} />{isZh ? '这是什么？' : 'How this works'}
            <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </button>
        )}
      </div>
      {open && (
        <section style={{ marginTop: 10, maxWidth: 600, background: S.soft, border: 'none', borderLeft: `1px solid ${S.goldDecor}`, borderRadius: 0, boxShadow: 'none',
          padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10, boxSizing: 'border-box' }}>
          {explain ? (
            <div style={{ fontSize: 14, lineHeight: 1.7, color: S.ink2 }}>{explain}</div>
          ) : steps.length > 0 && (
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: S.ink, lineHeight: 1.8 }}>
              {steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
          {tour.available && !tour.demo && <TourCatalogue />}
        </section>
      )}
    </div>
  );
}

export const card = { background: S.card, border: `1px solid ${S.line}`, borderRadius: S.radius, boxShadow: S.shadow };
// Luxe buttons: primary = black with ivory type, secondary = 1px black outline; square, letter-spaced.
export const btnPrimary = (busy) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', borderRadius: S.radius, border: `1px solid ${S.ink}`,
  background: S.ink, color: S.paper, fontSize: 12.5, fontWeight: 400, letterSpacing: '.14em', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
  fontFamily: 'inherit', whiteSpace: 'nowrap',
});
export const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px', borderRadius: S.radius, border: `1px solid ${S.ink}`,
  background: 'transparent', color: S.ink, fontSize: 12.5, fontWeight: 400, letterSpacing: '.14em', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
};
export const btnLink = { background: 'none', border: 'none', padding: 0, color: S.gold, fontSize: 12.5, fontWeight: 400, letterSpacing: '.08em', cursor: 'pointer', fontFamily: 'inherit' };
export const field = {
  font: 'inherit', fontSize: 13.5, border: `1px solid ${S.line}`, borderRadius: S.radius, padding: '7px 10px', background: S.card,
  minWidth: 0, boxSizing: 'border-box', width: '100%', color: S.ink,
};
export const sectionTitle = { fontFamily: S.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.2, color: S.ink, margin: '0 0 12px' };

export function NotDeployed({ what, isZh = true }) {
  return (
    <div style={{ padding: '18px 16px', borderRadius: S.radius, background: S.soft, border: `1px dashed ${S.line}`, color: S.sub, fontSize: 13, lineHeight: 1.7 }}>
      {isZh ? `${what || '这部分'}部署后可用。` : `${what || 'This part'} will be available after the next deploy.`}
    </div>
  );
}

// "今天 18:00 前" / "已逾期 3 小时" / "10/18 10:00 前"
export const dueText = (dueAt, overdue, isZh = true) => {
  const d = toDate(dueAt);
  if (!d) return '';
  const now = Date.now();
  if (overdue || d.getTime() < now) {
    const h = Math.max(1, Math.round((now - d.getTime()) / 3600000));
    return overText(h, isZh).replace('已超', '已逾期');
  }
  // "today" and the clock time are Pacific, not the browser's
  const w = toPacificWallClock(d);
  const sameDay = w.toDateString() === toPacificWallClock(new Date()).toDateString();
  const hm = `${String(w.getHours()).padStart(2, '0')}:${String(w.getMinutes()).padStart(2, '0')}`;
  if (sameDay) return isZh ? `今天 ${hm} 前` : `Today by ${hm}`;
  return isZh ? `${fmtMDHM(d)} 前` : `By ${fmtMDHM(d)}`;
};

// Button text for a 下一步 card.
export const actionLabel = (a, isZh = true) => {
  const t = (zh, en) => (isZh ? zh : en);
  const args = (a && a.action_args) || {};
  switch (a && a.action) {
    case 'contact': return t('写联系记录', 'Log contact');
    case 'meeting_check': return args.purpose === 'post_meeting' ? t('写见面后回访', 'Log follow-up') : t('写见面前记录', 'Log pre-meeting call');
    case 'todo': return args.rectification_id ? t('标记整改完成', 'Mark done') : t('标记完成', 'Mark done');
    case 'step': return t('去完成这一步', 'Do this step');
    case 'report': return t('打开报告', 'Open report');
    case 'complaint': return t('处理投诉', 'Handle complaint');
    case 'interval': return t('去确认', 'Review');
    case 'review': return t('去审核', 'Review');
    case 'recommend': return t('去推荐', 'Recommend');
    case 'schedule': return t('安排约会', 'Schedule');
    default: return t('去处理', 'Open');
  }
};

// ── fallback 下一步 (no /workspace yet) ───────────────────────────────────
// Built from the phase-1 service-status + meeting-checks so the tab still says
// something useful before the round-2 backend lands.
export function fallbackNextActions(svc, meetings, isZh = true) {
  const t = (zh, en) => (isZh ? zh : en);
  const out = [];
  if (!svc) return out;
  const comp = svc.complaints || {};
  if (Number(comp.urgent_open) > 0) {
    out.push({ key: 'complaint', severity: 'red', action: 'complaint', overdue: true,
      title: t(`有 ${comp.urgent_open} 条紧急投诉没处理完`, 'Urgent complaint open'),
      why: t('明显不满或严重投诉 = 红色预警，主管直接介入，问题必须闭环。', 'An urgent complaint is a red flag — close it.') });
  }
  const paused = svc.pause_until && toDate(svc.pause_until) && toDate(svc.pause_until).getTime() >= Date.now();
  if (svc.in_focus_period !== false && !paused) {
    const over = Number(svc.overdue_hours) > 0;
    const due = toDate(svc.next_due_at);
    if (over) {
      out.push({ key: 'contact48', severity: 'red', action: 'contact', overdue: true, due_at: svc.next_due_at,
        action_args: { purpose: 'followup_48h' },
        title: t(`${Number(svc.interval_hours) || 48} 小时联系已超时`, 'Contact overdue'),
        why: t('前三个月每 48 小时至少一次有效联系，不能因为暂时没有合适的人就停止服务。', 'At least one real contact every 48h in the first 3 months.') });
    } else if (due && due.getTime() - Date.now() < 24 * 3600000) {
      out.push({ key: 'contact48', severity: 'amber', action: 'contact', due_at: svc.next_due_at,
        action_args: { purpose: svc.last_contact_at ? 'followup_48h' : 'first_contact' },
        title: svc.last_contact_at ? t('今天要联系 TA', 'Contact due today') : t('做第一次联系', 'Make the first contact'),
        why: t('联系要有实际内容：沟通了什么、会员反馈、下一步。简单问候不算。', 'A greeting does not count — say what was discussed and the next step.') });
    }
  }
  const todos = svc.todos || {};
  if (Number(todos.overdue) > 0) {
    out.push({ key: 'todo', severity: 'amber', action: 'todo', overdue: true,
      title: t(`${todos.overdue} 件备忘录已逾期`, `${todos.overdue} to-dos overdue`),
      why: t('答应会员的事必须进 CRM 并按时完成；超时由主管跟进。', 'Promises must be logged and done on time.') });
  }
  (meetings || []).forEach((m) => {
    if (!m || !m.checks) return;
    const at = toDate(m.scheduled_at);
    const hoursTo = at ? (at.getTime() - Date.now()) / 3600000 : null;
    if (!m.ended && hoursTo != null && hoursTo <= 24 && hoursTo > -3) {
      if (!(m.checks.pre_self && m.checks.pre_self.done)) {
        out.push({ key: `pre_self_${m.dating_schedule_id}`, severity: 'amber', action: 'meeting_check', due_at: m.scheduled_at,
          action_args: { purpose: 'pre_meeting', dating_schedule_id: m.dating_schedule_id },
          title: t(`见面前电话 · 本会员（和 ${m.partner_label || '对方'}）`, 'Pre-meeting call · this member'),
          why: t('见面前 24 小时内要打交叉电话，电话后要有文字记录。', 'Cross calls within 24h before the meeting, with a written record.') });
      }
      if (!(m.checks.pre_partner && m.checks.pre_partner.done) && m.partner_profile_id) {
        out.push({ key: `pre_partner_${m.dating_schedule_id}`, severity: 'amber', action: 'meeting_check', due_at: m.scheduled_at,
          action_args: { purpose: 'cross_call', dating_schedule_id: m.dating_schedule_id, target_profile_id: m.partner_profile_id, target_name: m.partner_label },
          title: t(`交叉电话 · 对方会员 ${m.partner_label || ''}`, 'Cross call · partner'),
          why: t('女方老师打给男士、男方老师打给女士，确认意愿、期待和雷区。', 'Each coach calls the other member before the meeting.') });
      }
    }
    if (m.ended && !(m.checks.post_self && m.checks.post_self.done)) {
      out.push({ key: `post_${m.dating_schedule_id}`, severity: 'amber', action: 'meeting_check',
        action_args: { purpose: 'post_meeting', dating_schedule_id: m.dating_schedule_id },
        title: t(`约会后回访（和 ${m.partner_label || '对方'}）`, 'Post-meeting follow-up'),
        why: t('见面后 24–48 小时内电话回访：印象、顾虑、是否愿意第二次见面。', 'Call within 24–48h after the meeting.') });
    }
  });
  const rep = svc.report;
  if (rep && rep.id && ['draft', 'returned', 'overdue'].includes(reportStatusKey(rep))) {
    const rk = reportStatusKey(rep);
    out.push({ key: 'report', severity: rk === 'draft' ? 'normal' : 'amber', action: 'report', due_at: rep.due_at, overdue: rk === 'overdue',
      action_args: { report_id: rep.id },
      title: t(`月度服务报告 · ${REPORT_STATUS[rk].zh}`, `Monthly report · ${REPORT_STATUS[rk].en}`),
      why: t('每 30 天一份服务报告，主管审核后发给会员。', 'A service report every 30 days, reviewed before it goes out.') });
  }
  if (svc.pending_interval_request && svc.can_approve) {
    out.push({ key: 'interval', severity: 'normal', action: 'interval',
      title: t('联系频率调整申请待你确认', 'Frequency change to approve'),
      why: t('会员要求降低沟通频率的，记录后由主管确认才生效。', 'A lower contact frequency needs a supervisor’s approval.') });
  }
  const rank = { red: 0, amber: 1, normal: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

export function fallbackLight(svc, isZh = true) {
  if (!svc) return { light: null, reasons: [] };
  const t = (zh, en) => (isZh ? zh : en);
  const reasons = [];
  let light = 'green';
  if (Number(svc.complaints && svc.complaints.urgent_open) > 0) { light = 'red'; reasons.push(t('有紧急投诉未处理', 'Urgent complaint open')); }
  if (Number(svc.overdue_hours) > 0) { light = 'red'; reasons.push(t('48 小时联系已超时', 'Contact overdue')); }
  if (Number(svc.todos && svc.todos.overdue) > 0) { if (light === 'green') light = 'yellow'; reasons.push(t('有逾期的备忘录', 'Overdue to-dos')); }
  if (light === 'green') reasons.push(t('时效正常，有明确的下一步', 'On schedule'));
  return { light, reasons };
}
